<template>
  <div class="sp-page">

    <!-- ============================================ -->
    <!-- HERO HEADER                                  -->
    <!-- ============================================ -->
    <!-- <header class="sp-hero"> ... </header> -->

    <!-- ============================================ -->
    <!-- STATS CARDS                                  -->
    <!-- ============================================ -->
    <div class="sp-stats">
      <div class="sp-stat is-sale">
        <div class="sp-stat-icon"><b-icon icon="tag-fill"></b-icon></div>
        <div class="sp-stat-body">
          <div class="sp-stat-value">{{ saleItems.length }}</div>
          <div class="sp-stat-label">Items on sale</div>
        </div>
      </div>
      <div class="sp-stat is-clear">
        <div class="sp-stat-icon"><b-icon icon="archive-fill"></b-icon></div>
        <div class="sp-stat-body">
          <div class="sp-stat-value">{{ clearanceItems.length }}</div>
          <div class="sp-stat-label">On clearance</div>
        </div>
      </div>
      <div class="sp-stat is-disc">
        <div class="sp-stat-icon"><b-icon icon="cash-coin"></b-icon></div>
        <div class="sp-stat-body">
          <div class="sp-stat-value">{{ discountItems.length }}</div>
          <div class="sp-stat-label">Promos</div>
        </div>
      </div>
      <div class="sp-stat is-total">
        <div class="sp-stat-icon"><b-icon icon="cash-stack"></b-icon></div>
        <div class="sp-stat-body">
          <div class="sp-stat-value">${{ totalCombinedDiscount }}</div>
          <div class="sp-stat-label">Total markdown</div>
        </div>
      </div>
      <div class="sp-stat is-avg">
        <div class="sp-stat-icon"><b-icon icon="percent"></b-icon></div>
        <div class="sp-stat-body">
          <div class="sp-stat-value">{{ avgCombinedDiscount }}%</div>
          <div class="sp-stat-label">Avg discount</div>
        </div>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- MAIN TABS                                    -->
    <!-- ============================================ -->
    <nav class="sp-tabs" role="tablist">
      <button type="button" class="sp-tab" :class="{ 'is-active': activeTab === 0 }" @click="activeTab = 0">
        <span class="sp-tab-icon"><b-icon icon="plus-circle"></b-icon></span>
        <span class="sp-tab-label">Create</span>
      </button>
      <button type="button" class="sp-tab is-sale" :class="{ 'is-active': activeTab === 1 }" @click="activeTab = 1">
        <span class="sp-tab-icon"><b-icon icon="tag-fill"></b-icon></span>
        <span class="sp-tab-label">On sale</span>
        <span class="sp-tab-count">{{ saleItems.length }}</span>
      </button>
      <button type="button" class="sp-tab is-clear" :class="{ 'is-active': activeTab === 2 }" @click="activeTab = 2">
        <span class="sp-tab-icon"><b-icon icon="archive-fill"></b-icon></span>
        <span class="sp-tab-label">On clearance</span>
        <span class="sp-tab-count">{{ clearanceItems.length }}</span>
      </button>
      <button type="button" class="sp-tab is-disc" :class="{ 'is-active': activeTab === 3 }" @click="activeTab = 3">
        <span class="sp-tab-icon"><b-icon icon="cash-coin"></b-icon></span>
        <span class="sp-tab-label">Promo</span>
        <span class="sp-tab-count">{{ discountItems.length }}</span>
      </button>
    </nav>

    <!-- ============================================ -->
    <!-- TAB 0 — CREATE                               -->
    <!-- ============================================ -->
    <section v-show="activeTab === 0" class="sp-panel">

      <!-- Mode switcher -->
      <div class="sp-mode-tabs">
        <button class="sp-mode-tab" :class="{ 'is-active': createMode === 'products' }" @click="switchCreateMode('products')">
          <b-icon icon="grid-fill"></b-icon><span>Products</span>
        </button>
        <button class="sp-mode-tab" :class="{ 'is-active': createMode === 'categories' }" @click="switchCreateMode('categories')">
          <b-icon icon="folder-fill"></b-icon><span>Categories</span>
        </button>
        <button class="sp-mode-tab" :class="{ 'is-active': createMode === 'subcategories' }" @click="switchCreateMode('subcategories')">
          <b-icon icon="diagram-3-fill"></b-icon><span>Subcategories</span>
        </button>
      </div>

      <!-- ======================================== -->
      <!-- PRODUCTS MODE                            -->
      <!-- ======================================== -->
      <div v-if="createMode === 'products'" class="sp-card">

        <div class="sp-toolbar">
          <div class="sp-segment">
            <button class="sp-seg-btn" :class="{ 'is-active': createForm.actionType === 'Sale' }" @click="createForm.actionType = 'Sale'">
              <b-icon icon="tag-fill"></b-icon><span>Sale</span>
            </button>
            <button class="sp-seg-btn" :class="{ 'is-active': createForm.actionType === 'Clearance' }" @click="createForm.actionType = 'Clearance'">
              <b-icon icon="archive-fill"></b-icon><span>Clearance</span>
            </button>
            <button class="sp-seg-btn" :class="{ 'is-active': createForm.actionType === 'Promo' }" @click="createForm.actionType = 'Promo'">
              <b-icon icon="cash-coin"></b-icon><span>Promo</span>
            </button>
          </div>

          <div class="sp-vdivider"></div>

          <b-form-select v-model="filterCategoryId" :options="categoryOptions" class="sp-filter" @change="onFilterChange"></b-form-select>

          <b-form-select v-model="filterSubcategoryId" :options="subcategoryOptions" class="sp-filter" :disabled="!filterCategoryId" @change="onFilterChange"></b-form-select>

          <b-input-group class="sp-search">
            <b-form-input v-model="searchQuery" placeholder="Search SKU or name…" @keyup.enter="onFilterChange"></b-form-input>
            <b-input-group-append>
              <b-button class="sp-btn-ghost" @click="onFilterChange"><b-icon icon="search"></b-icon></b-button>
            </b-input-group-append>
          </b-input-group>

          <b-button class="sp-btn-ghost sp-btn-icon" @click="onFilterChange"><b-icon icon="arrow-repeat"></b-icon></b-button>

          <transition name="sp-pop">
            <div v-if="selectedProducts.length > 0" class="sp-chip is-selected">
              <b-icon icon="check2-circle"></b-icon>
              <span>{{ selectedProducts.length }} selected</span>
              <b-icon icon="x-circle-fill" class="sp-chip-x" @click="clearSelection"></b-icon>
            </div>
          </transition>
        </div>

        <div class="sp-scope" v-if="filterCategoryId || filterSubcategoryId || searchQuery">
          <div class="sp-scope-left">
            <b-icon icon="funnel-fill" class="sp-scope-icon"></b-icon>
            <div>
              <strong>{{ filterSummaryText }}</strong>
              <div class="sp-scope-sub">
                <span v-if="totalFilteredCount !== null">{{ totalFilteredCount.toLocaleString() }} products match</span>
                <span v-else>Counting…</span>
              </div>
            </div>
          </div>
          <div class="sp-scope-right">
            <b-button size="sm" :class="applyScope === 'all' ? 'sp-btn-primary' : 'sp-btn-ghost'" @click="setScope('all')">
              <b-icon icon="layers-fill"></b-icon> Apply to all {{ totalFilteredCount || '' }}
            </b-button>
            <b-button size="sm" :class="applyScope === 'visible' ? 'sp-btn-primary' : 'sp-btn-ghost'" @click="setScope('visible')">
              <b-icon icon="eye-fill"></b-icon> Only visible ({{ sourceProducts.length }})
            </b-button>
          </div>
        </div>

        <div v-if="loadingSource && sourceProducts.length === 0" class="sp-loading">
          <b-spinner variant="primary"></b-spinner>
          <p>Loading products…</p>
        </div>

        <div v-else-if="sourceProducts.length > 0" class="sp-table-wrap" ref="scrollContainer" @scroll="onTableScroll">
          <table class="sp-table">
            <thead>
              <tr>
                <th class="col-check">
                  <b-form-checkbox v-model="selectAllVisible" @change="toggleSelectAllVisible" size="sm"></b-form-checkbox>
                </th>
                <th class="col-img">IMG</th>
                <th class="col-sku">SKU</th>
                <th class="col-name">Product</th>
                <th class="col-cat">Category / Sub</th>
                <th class="col-price">Price</th>
                <th class="col-status">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, idx) in sourceProducts"
                :key="item.sku + '-src-' + idx"
                :class="{ 'is-selected': selectedProducts.includes(item.sku) }"
                @click="toggleProduct(item.sku)"
                class="sp-row"
              >
                <td class="col-check" @click.stop>
                  <b-form-checkbox v-model="selectedProducts" :value="item.sku" size="sm"></b-form-checkbox>
                </td>
                <td class="col-img">
                  <div class="sp-thumb">
                    <img v-if="getProductImage(item)" :src="getProductImage(item)" :alt="item.sku" @error="handleImageError" />
                    <div v-else class="sp-thumb-empty"><b-icon icon="image"></b-icon></div>
                  </div>
                </td>
                <td class="col-sku"><span class="sp-sku">{{ item.sku }}</span></td>
                <td class="col-name"><div class="sp-pname">{{ item.product_name }}</div></td>
                <td class="col-cat">
                  <div class="sp-cat-stack">
                    <span class="sp-cat-primary">{{ item.category_name || '—' }}</span>
                    <span v-if="getSubcategoryName(item)" class="sp-cat-secondary">{{ getSubcategoryName(item) }}</span>
                  </div>
                </td>
                <td class="col-price"><span class="sp-price">${{ formatPrice(item.selling_price) }}</span></td>
                <td class="col-status">
                  <span v-if="item.sale_type === 'Sale'" class="sp-chip is-sale">SALE</span>
                  <span v-else-if="item.sale_type === 'Clearance'" class="sp-chip is-clear">CLEARANCE</span>
                  <span v-else-if="item.sale_type === 'Promo'" class="sp-chip is-disc">PROMO</span>
                  <span v-else class="sp-chip">NORMAL</span>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-if="loadingMore" class="sp-load-more">
            <b-spinner small></b-spinner><span>Loading more…</span>
          </div>
          <div v-else-if="!hasMore && sourceProducts.length > 0" class="sp-end-marker">
            — All {{ sourceProducts.length }} products loaded —
          </div>
        </div>

        <div v-else class="sp-empty">
          <b-icon icon="inbox" font-scale="3"></b-icon>
          <p>No products found.</p>
        </div>
      </div>

      <!-- ======================================== -->
      <!-- BULK MODE                                -->
      <!-- ======================================== -->
      <div v-else class="sp-card">

        <div class="sp-toolbar">
          <div class="sp-segment">
            <button class="sp-seg-btn" :class="{ 'is-active': createForm.actionType === 'Sale' }" @click="createForm.actionType = 'Sale'">
              <b-icon icon="tag-fill"></b-icon><span>Sale</span>
            </button>
            <button class="sp-seg-btn" :class="{ 'is-active': createForm.actionType === 'Clearance' }" @click="createForm.actionType = 'Clearance'">
              <b-icon icon="archive-fill"></b-icon><span>Clearance</span>
            </button>
            <button class="sp-seg-btn" :class="{ 'is-active': createForm.actionType === 'Promo' }" @click="createForm.actionType = 'Promo'">
              <b-icon icon="cash-coin"></b-icon><span>Promo</span>
            </button>
          </div>

          <div class="sp-vdivider"></div>

          <b-input-group class="sp-search">
            <b-form-input v-model="bulkSearchQuery" :placeholder="createMode === 'categories' ? 'Search categories…' : 'Search subcategories…'"></b-form-input>
            <b-input-group-append>
              <b-button class="sp-btn-ghost"><b-icon icon="search"></b-icon></b-button>
            </b-input-group-append>
          </b-input-group>

          <transition name="sp-pop">
            <div v-if="bulkSelectedIds.length > 0" class="sp-chip is-selected">
              <b-icon icon="check2-circle"></b-icon>
              <span>{{ bulkSelectedIds.length }} selected</span>
              <b-icon icon="x-circle-fill" class="sp-chip-x" @click="bulkSelectedIds = []"></b-icon>
            </div>
          </transition>
        </div>

        <div v-if="createMode === 'categories'" class="sp-bulk-list">
          <div
            v-for="cat in filteredCategoriesForBulk"
            :key="cat.value"
            class="sp-bulk-item"
            :class="{ 'is-selected': bulkSelectedIds.includes(cat.value) }"
            @click="toggleBulkId(cat.value)"
          >
            <b-form-checkbox :checked="bulkSelectedIds.includes(cat.value)" @change="toggleBulkId(cat.value)" @click.native.stop></b-form-checkbox>
            <div class="sp-bulk-body">
              <div class="sp-bulk-title">{{ cat.text }}</div>
              <div class="sp-bulk-sub">Category ID: {{ cat.value }}</div>
            </div>
            <span class="sp-chip">Whole category</span>
          </div>

          <div v-if="filteredCategoriesForBulk.length === 0" class="sp-empty">
            <b-icon icon="inbox" font-scale="2"></b-icon><p>No categories found.</p>
          </div>
        </div>

        <div v-else class="sp-bulk-list">
          <template v-for="group in groupedSubcategoriesForBulk">
            <div :key="'g-' + group.categoryId" class="sp-bulk-group">
              <b-icon icon="folder-fill"></b-icon> {{ group.categoryName }}
            </div>
            <div
              v-for="sub in group.items"
              :key="sub.id"
              class="sp-bulk-item is-indent"
              :class="{ 'is-selected': bulkSelectedIds.includes(sub.id) }"
              @click="toggleBulkId(sub.id)"
            >
              <b-form-checkbox :checked="bulkSelectedIds.includes(sub.id)" @change="toggleBulkId(sub.id)" @click.native.stop></b-form-checkbox>
              <div class="sp-bulk-body">
                <div class="sp-bulk-title">{{ sub.subcategory }}</div>
              </div>
            </div>
          </template>

          <div v-if="groupedSubcategoriesForBulk.length === 0" class="sp-empty">
            <b-icon icon="inbox" font-scale="2"></b-icon><p>No subcategories found.</p>
          </div>
        </div>

        <div v-if="bulkSelectedIds.length > 0" class="sp-bulk-confirm">
          <b-icon icon="info-circle-fill"></b-icon>
          <span>
            Applying <strong>{{ bulkDiscountLabel }}</strong> <strong>{{ createForm.actionType }}</strong>
            to
            <strong>
              {{ bulkSelectedIds.length }}
              {{ createMode === 'categories'
                  ? (bulkSelectedIds.length > 1 ? 'categories' : 'category')
                  : (bulkSelectedIds.length > 1 ? 'subcategories' : 'subcategory') }}
            </strong>
            — this affects <em>all products inside them</em>.
          </span>
        </div>
      </div>

      <!-- CONFIGURE CTA -->
      <div class="sp-config-cta" :class="{ 'is-disabled': !submitBarVisibleComputed }">
        <div class="sp-config-cta-icon"><b-icon icon="sliders"></b-icon></div>
        <div class="sp-config-cta-body">
          <div class="sp-config-cta-title">Configure {{ createForm.actionType }}</div>
          <div class="sp-config-cta-sub">
            {{ submitBarVisibleComputed ? `Ready to apply ${scopeSummary}` : 'Pick products or a bulk scope above to continue' }}
          </div>
        </div>
        <b-button class="sp-btn-primary" size="lg" :disabled="!submitBarVisibleComputed" @click="openConfigModal">
          <b-icon icon="sliders"></b-icon> Configure &amp; Apply
        </b-button>
      </div>

      <!-- STICKY SUBMIT BAR -->
      <div class="sp-submit-bar" v-if="submitBarVisibleComputed">
        <div class="sp-submit-info">
          <b-icon icon="info-circle-fill" font-scale="1.2"></b-icon>
          <span v-if="createMode === 'products'">
            Applying <strong>{{ scopeSummary }}</strong> to
            <strong>
              {{ applyScope === 'all'
                  ? (totalFilteredCount || 0).toLocaleString() + ' products (filter)'
                  : selectedProducts.length + ' selected product(s)' }}
            </strong>
          </span>
          <span v-else>
            Applying <strong>{{ scopeSummary }}</strong> to
            <strong>
              {{ bulkSelectedIds.length }}
              {{ createMode === 'categories'
                  ? (bulkSelectedIds.length > 1 ? 'categories' : 'category')
                  : (bulkSelectedIds.length > 1 ? 'subcategories' : 'subcategory') }}
            </strong> (all products inside)
          </span>
        </div>
        <div class="sp-submit-actions">
          <b-button class="sp-btn-ghost-dark" size="lg" @click="resetCreateForm">Reset</b-button>
          <b-button :class="applyButtonClass" size="lg" @click="openConfigModal">
            <b-icon :icon="applyButtonIcon" class="mr-2"></b-icon> Configure {{ createForm.actionType }}
          </b-button>
        </div>
      </div>
    </section>

    <!-- ============================================ -->
    <!-- TAB 1 — ON SALE                              -->
    <!-- ============================================ -->
    <section v-show="activeTab === 1" class="sp-panel">
      <div class="sp-card">
        <div class="sp-view-toolbar">
          <div class="sp-view-toolbar-left">
            <b-form-select v-model="saleViewFilterCategory" :options="categoryOptions" class="sp-filter" @change="fetchSaleItems"></b-form-select>
            <b-input-group class="sp-search">
              <b-form-input v-model="saleViewSearch" placeholder="Search…" @keyup.enter="fetchSaleItems"></b-form-input>
              <b-input-group-append>
                <b-button class="sp-btn-ghost" @click="fetchSaleItems"><b-icon icon="search"></b-icon></b-button>
              </b-input-group-append>
            </b-input-group>
            <b-button class="sp-btn-ghost sp-btn-icon" @click="fetchSaleItems"><b-icon icon="arrow-repeat"></b-icon></b-button>
          </div>
          <div class="sp-view-toolbar-right">
            <span class="sp-chip is-sale">{{ saleItems.length }} items · ${{ totalSaleDiscount }} · Avg {{ avgSaleDiscount }}%</span>
          </div>
        </div>

        <div v-if="loadingSale" class="sp-loading"><b-spinner variant="success"></b-spinner></div>

        <div v-else-if="saleItems.length > 0" class="sp-table-wrap">
          <table class="sp-table">
            <thead>
              <tr>
                <th class="col-img">IMG</th>
                <th class="col-sku">SKU</th>
                <th class="col-name">Product</th>
                <th class="col-cat">Category / Sub</th>
                <th class="col-price">Original</th>
                <th class="col-disc">Disc %</th>
                <th class="col-sale">Sale price</th>
                <th class="col-max">Max %</th>
                <th class="col-start">Starts</th>
                <th class="col-end">Ends</th>
                <th class="col-act">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in saleItems" :key="item.sku + '-sale-' + idx">
                <td class="col-img">
                  <div class="sp-thumb">
                    <img v-if="getProductImage(item)" :src="getProductImage(item)" @error="handleImageError" />
                    <div v-else class="sp-thumb-empty"><b-icon icon="image"></b-icon></div>
                  </div>
                </td>
                <td class="col-sku"><span class="sp-sku">{{ item.sku }}</span></td>
                <td class="col-name"><div class="sp-pname">{{ item.product_name }}</div></td>
                <td class="col-cat">
                  <div class="sp-cat-stack">
                    <span class="sp-cat-primary">{{ item.category_name || '—' }}</span>
                    <span v-if="getSubcategoryName(item)" class="sp-cat-secondary">{{ getSubcategoryName(item) }}</span>
                  </div>
                </td>
                <td class="col-price"><span class="sp-strike">${{ formatPrice(item.selling_price) }}</span></td>
                <td class="col-disc"><span class="sp-chip is-discount-tag">{{ calculateDiscountPercent(item) }}%</span></td>
                <td class="col-sale"><strong class="sp-price-teal">${{ formatPrice(item.special_discount_price) }}</strong></td>
                <td class="col-max"><span class="sp-chip is-warn">{{ item.max_discount_percentage || 0 }}%</span></td>
                <td class="col-start"><span :class="{ 'is-future': isScheduled(item) }">{{ formatDate(item.special_discount_start_date) }}</span></td>
                <td class="col-end"><span :class="{ 'is-expired': isExpired(item) }">{{ formatDate(item.special_discount_end_date) }}</span></td>
                <td class="col-act">
                  <b-button size="sm" class="sp-btn-ghost sp-btn-icon" @click="editItemSale(item)" title="Edit"><b-icon icon="pencil-fill"></b-icon></b-button>
                  <b-button size="sm" class="sp-btn-ghost sp-btn-icon is-danger" @click="removeSale(item)" title="Remove"><b-icon icon="x-circle-fill"></b-icon></b-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="sp-empty">
          <b-icon icon="tag" font-scale="4"></b-icon>
          <h5>No items on sale</h5>
          <p>Use the Create tab to add items to sale.</p>
        </div>
      </div>
    </section>

    <!-- ============================================ -->
    <!-- TAB 2 — ON CLEARANCE                         -->
    <!-- ============================================ -->
    <section v-show="activeTab === 2" class="sp-panel">
      <div class="sp-card">
        <div class="sp-view-toolbar">
          <div class="sp-view-toolbar-left">
            <b-form-select v-model="clearViewFilterCategory" :options="categoryOptions" class="sp-filter" @change="fetchClearanceItems"></b-form-select>
            <b-input-group class="sp-search">
              <b-form-input v-model="clearViewSearch" placeholder="Search…" @keyup.enter="fetchClearanceItems"></b-form-input>
              <b-input-group-append>
                <b-button class="sp-btn-ghost" @click="fetchClearanceItems"><b-icon icon="search"></b-icon></b-button>
              </b-input-group-append>
            </b-input-group>
            <b-button class="sp-btn-ghost sp-btn-icon" @click="fetchClearanceItems"><b-icon icon="arrow-repeat"></b-icon></b-button>
          </div>
          <div class="sp-view-toolbar-right">
            <span class="sp-chip is-clear">{{ clearanceItems.length }} items · ${{ totalClearanceDiscount }} · Avg {{ avgClearanceDiscount }}%</span>
          </div>
        </div>

        <div v-if="loadingClear" class="sp-loading"><b-spinner variant="danger"></b-spinner></div>

        <div v-else-if="clearanceItems.length > 0" class="sp-table-wrap">
          <table class="sp-table">
            <thead>
              <tr>
                <th class="col-img">IMG</th>
                <th class="col-sku">SKU</th>
                <th class="col-name">Product</th>
                <th class="col-cat">Category / Sub</th>
                <th class="col-price">Original</th>
                <th class="col-disc">Disc %</th>
                <th class="col-sale">Clearance</th>
                <th class="col-max">Max %</th>
                <th class="col-start">Starts</th>
                <th class="col-end">Ends</th>
                <th class="col-act">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in clearanceItems" :key="item.sku + '-clr-' + idx">
                <td class="col-img">
                  <div class="sp-thumb">
                    <img v-if="getProductImage(item)" :src="getProductImage(item)" @error="handleImageError" />
                    <div v-else class="sp-thumb-empty"><b-icon icon="image"></b-icon></div>
                  </div>
                </td>
                <td class="col-sku"><span class="sp-sku">{{ item.sku }}</span></td>
                <td class="col-name"><div class="sp-pname">{{ item.product_name }}</div></td>
                <td class="col-cat">
                  <div class="sp-cat-stack">
                    <span class="sp-cat-primary">{{ item.category_name || '—' }}</span>
                    <span v-if="getSubcategoryName(item)" class="sp-cat-secondary">{{ getSubcategoryName(item) }}</span>
                  </div>
                </td>
                <td class="col-price"><span class="sp-strike">${{ formatPrice(item.selling_price) }}</span></td>
                <td class="col-disc"><span class="sp-chip is-discount-tag">{{ calculateDiscountPercent(item) }}%</span></td>
                <td class="col-sale"><strong class="sp-price-danger">${{ formatPrice(item.special_discount_price) }}</strong></td>
                <td class="col-max"><span class="sp-chip is-warn">{{ item.max_discount_percentage || 0 }}%</span></td>
                <td class="col-start"><span :class="{ 'is-future': isScheduled(item) }">{{ formatDate(item.special_discount_start_date) }}</span></td>
                <td class="col-end"><span :class="{ 'is-expired': isExpired(item) }">{{ formatDate(item.special_discount_end_date) }}</span></td>
                <td class="col-act">
                  <b-button size="sm" class="sp-btn-ghost sp-btn-icon" @click="editItemSale(item)" title="Edit"><b-icon icon="pencil-fill"></b-icon></b-button>
                  <b-button size="sm" class="sp-btn-ghost sp-btn-icon is-danger" @click="removeSale(item)" title="Remove"><b-icon icon="x-circle-fill"></b-icon></b-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="sp-empty">
          <b-icon icon="archive" font-scale="4"></b-icon>
          <h5>No items on clearance</h5>
          <p>Use the Create tab to add items to clearance.</p>
        </div>
      </div>
    </section>

    <!-- ============================================ -->
    <!-- TAB 3 — PROMO                                -->
    <!-- ============================================ -->
    <section v-show="activeTab === 3" class="sp-panel">
      <div class="sp-card">
        <div class="sp-view-toolbar">
          <div class="sp-view-toolbar-left">
            <b-form-select v-model="discViewFilterCategory" :options="categoryOptions" class="sp-filter" @change="fetchDiscountItems"></b-form-select>
            <b-input-group class="sp-search">
              <b-form-input v-model="discViewSearch" placeholder="Search…" @keyup.enter="fetchDiscountItems"></b-form-input>
              <b-input-group-append>
                <b-button class="sp-btn-ghost" @click="fetchDiscountItems"><b-icon icon="search"></b-icon></b-button>
              </b-input-group-append>
            </b-input-group>
            <b-button class="sp-btn-ghost sp-btn-icon" @click="fetchDiscountItems"><b-icon icon="arrow-repeat"></b-icon></b-button>
          </div>
          <div class="sp-view-toolbar-right">
            <span class="sp-chip is-disc">{{ discountItems.length }} items · ${{ totalDiscountAmount }} · Avg {{ avgDiscountAmount }}</span>
          </div>
        </div>

        <div v-if="loadingDisc" class="sp-loading"><b-spinner variant="primary"></b-spinner></div>

        <div v-else-if="discountItems.length > 0" class="sp-table-wrap">
          <table class="sp-table">
            <thead>
              <tr>
                <th class="col-img">IMG</th>
                <th class="col-sku">SKU</th>
                <th class="col-name">Product</th>
                <th class="col-cat">Category / Sub</th>
                <th class="col-price">Original</th>
                <th class="col-disc">Amount off</th>
                <th class="col-sale">Final price</th>
                <th class="col-max">Eff. %</th>
                <th class="col-start">Starts</th>
                <th class="col-end">Ends</th>
                <th class="col-act">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in discountItems" :key="item.sku + '-disc-' + idx">
                <td class="col-img">
                  <div class="sp-thumb">
                    <img v-if="getProductImage(item)" :src="getProductImage(item)" @error="handleImageError" />
                    <div v-else class="sp-thumb-empty"><b-icon icon="image"></b-icon></div>
                  </div>
                </td>
                <td class="col-sku"><span class="sp-sku">{{ item.sku }}</span></td>
                <td class="col-name"><div class="sp-pname">{{ item.product_name }}</div></td>
                <td class="col-cat">
                  <div class="sp-cat-stack">
                    <span class="sp-cat-primary">{{ item.category_name || '—' }}</span>
                    <span v-if="getSubcategoryName(item)" class="sp-cat-secondary">{{ getSubcategoryName(item) }}</span>
                  </div>
                </td>
                <td class="col-price"><span class="sp-strike">${{ formatPrice(item.selling_price) }}</span></td>
                <td class="col-disc">
                  <span class="sp-chip is-discount-tag">-${{ formatPrice(calculateDiscountAmount(item)) }}</span>
                </td>
                <td class="col-sale"><strong class="sp-price-orange">${{ formatPrice(item.special_discount_price) }}</strong></td>
                <td class="col-max"><span class="sp-chip is-warn">{{ calculateDiscountPercent(item) }}%</span></td>
                <td class="col-start"><span :class="{ 'is-future': isScheduled(item) }">{{ formatDate(item.special_discount_start_date) }}</span></td>
                <td class="col-end"><span :class="{ 'is-expired': isExpired(item) }">{{ formatDate(item.special_discount_end_date) }}</span></td>
                <td class="col-act">
                  <b-button size="sm" class="sp-btn-ghost sp-btn-icon" @click="editItemSale(item)" title="Edit"><b-icon icon="pencil-fill"></b-icon></b-button>
                  <b-button size="sm" class="sp-btn-ghost sp-btn-icon is-danger" @click="removeSale(item)" title="Remove"><b-icon icon="x-circle-fill"></b-icon></b-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="sp-empty">
          <b-icon icon="cash-coin" font-scale="4"></b-icon>
          <h5>No Promo Available</h5>
          <p>Use the Create tab, choose Promo, and apply a fixed amount off.</p>
        </div>
      </div>
    </section>

    <!-- ============================================ -->
    <!-- CONFIGURE MODAL                              -->
    <!-- ============================================ -->
    <b-modal
      id="modal-configure"
      size="lg"
      centered
      hide-footer
      content-class="sp-modal"
      dialog-class="sp-dialog"
      @hidden="onConfigureModalHidden"
    >
      <template #modal-header>
        <div class="sp-modal-title">
          <span class="sp-modal-title-icon" :class="'is-' + createForm.actionType.toLowerCase()">
            <b-icon :icon="applyButtonIcon"></b-icon>
          </span>
          <div>
            <div class="sp-modal-title-main">Configure {{ createForm.actionType }}</div>
            <div class="sp-modal-title-sub">{{ configureModalSubtitle }}</div>
          </div>
        </div>
      </template>

      <div class="sp-modal-body">
        <div class="sp-modal-scope-banner" :class="'is-' + createForm.actionType.toLowerCase()">
          <b-icon icon="layers-fill"></b-icon>
          <div>
            <div class="sp-modal-scope-title">{{ configureScopeTitle }}</div>
            <div class="sp-modal-scope-sub">{{ configureScopeSub }}</div>
          </div>
        </div>

        <div class="sp-modal-grid">
          <div v-if="createForm.actionType !== 'Promo'" class="sp-field">
            <label class="sp-field-label">Discount percentage</label>
            <b-input-group size="lg">
              <b-form-input
                v-model.number="createForm.discountPercentage"
                type="number" min="0.01" max="100" step="0.01"
                placeholder="e.g. 15"
                @input="calculatePreview"
              ></b-form-input>
              <b-input-group-append><b-input-group-text class="sp-pct">%</b-input-group-text></b-input-group-append>
            </b-input-group>
          </div>

          <div v-else class="sp-field">
            <label class="sp-field-label">Discount amount (fixed)</label>
            <b-input-group size="lg">
              <b-input-group-prepend><b-input-group-text class="sp-pct">$</b-input-group-text></b-input-group-prepend>
              <b-form-input
                v-model.number="createForm.discountAmount"
                type="number" min="0.01" step="0.01"
                placeholder="e.g. 25.00"
                @input="calculatePreview"
              ></b-form-input>
            </b-input-group>
            <small class="sp-field-hint">Flat amount removed from each product's selling price.</small>
          </div>

          <div class="sp-field">
            <label class="sp-field-label">Max discount allowed</label>
            <b-input-group size="lg">
              <b-form-input v-model.number="createForm.maxDiscount" type="number" min="0" max="100" step="0.5"></b-form-input>
              <b-input-group-append><b-input-group-text class="sp-pct">%</b-input-group-text></b-input-group-append>
            </b-input-group>
          </div>

          <div class="sp-field" v-if="createForm.actionType !== 'Clearance'">
            <label class="sp-field-label">Sale starts on</label>
            <date-picker v-model="createForm.startDate" format="MM-DD-YYYY" value-type="format" class="sp-datepicker" :clearable="false"></date-picker>
          </div>

          <div class="sp-field" v-if="createForm.actionType === 'Sale'">
            <label class="sp-field-label">Sale ends on</label>
            <date-picker v-model="createForm.endDate" format="MM-DD-YYYY" value-type="format" class="sp-datepicker" :clearable="false"></date-picker>
          </div>

          <div v-else-if="createForm.actionType === 'Clearance'" class="sp-field">
            <label class="sp-field-label">Clearance duration</label>
            <div class="sp-readonly">90 days (auto-set)</div>
          </div>

          <div v-else class="sp-field">
            <label class="sp-field-label">Promo ends on</label>
            <date-picker v-model="createForm.endDate" format="MM-DD-YYYY" value-type="format" class="sp-datepicker" :clearable="false"></date-picker>
          </div>
        </div>

        <!-- ============================================ -->
        <!-- LIVE PREVIEW (now editable per row)          -->
        <!-- ============================================ -->
        <div v-if="createMode === 'products' && preview.length > 0" class="sp-preview">
          <div class="sp-preview-head">
            <b-icon icon="eye-fill"></b-icon>
            <strong>
              Preview (first 5 of
              {{ applyScope === 'all' ? totalFilteredCount : selectedProducts.length }})
            </strong>
            <span class="sp-preview-hint">Edit any row to fine-tune</span>
          </div>
          <table class="sp-preview-table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Original</th>
                <th v-if="createForm.actionType !== 'Promo'">Discount %</th>
                <th v-else>Amount off</th>
                <th>Price off</th>
                <th>{{ previewPriceColumnLabel }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(p, idx) in preview" :key="p.sku">
                <td><span class="sp-sku">{{ p.sku }}</span></td>
                <td><span class="sp-strike">${{ formatPrice(p.original) }}</span></td>

                <td v-if="createForm.actionType !== 'Promo'">
                  <b-input-group size="sm">
                    <b-form-input
                      v-model.number="p.percent"
                      type="number" min="0" max="100" step="0.01"
                      @input="onPreviewRowChange(p)"
                    ></b-form-input>
                    <b-input-group-append><b-input-group-text class="sp-pct-sm">%</b-input-group-text></b-input-group-append>
                  </b-input-group>
                </td>

                <td v-else>
                  <b-input-group size="sm">
                    <b-input-group-prepend><b-input-group-text class="sp-pct-sm">$</b-input-group-text></b-input-group-prepend>
                    <b-form-input
                      v-model.number="p.amount"
                      type="number" min="0" step="0.01"
                      @input="onPreviewRowChange(p)"
                    ></b-form-input>
                  </b-input-group>
                </td>

                <td><span class="sp-chip is-discount-tag">-${{ formatPrice(p.priceOff) }}</span></td>
                <td><strong :class="previewPriceClass">${{ formatPrice(p.salePrice) }}</strong></td>
                <td>
                  <b-button
                    size="sm"
                    class="sp-btn-ghost sp-btn-icon"
                    title="Reset to default"
                    @click="resetPreviewRow(p)"
                  >
                    <b-icon icon="arrow-counterclockwise"></b-icon>
                  </b-button>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="sp-preview-summary">
            <span><strong>{{ preview.length }}</strong> row(s) shown ·</span>
            <span><strong>{{ overriddenCount }}</strong> row(s) customised</span>
          </div>
        </div>

        <!-- Confirm / Apply -->
        <div class="sp-modal-foot">
          <b-button class="sp-btn-ghost" size="lg" @click="$bvModal.hide('modal-configure')">Cancel</b-button>
          <b-button :class="applyButtonClass" size="lg" @click="executeCreate" :disabled="!isCreateValidComputed || saving">
            <b-spinner v-if="saving" small class="mr-2"></b-spinner>
            <b-icon v-else :icon="applyButtonIcon" class="mr-2"></b-icon>
            {{ saving ? 'Saving…' : `Apply ${createForm.actionType}` }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- ============================================ -->
    <!-- EDIT MODAL                                   -->
    <!-- ============================================ -->
    <b-modal id="modal-edit-sale" size="md" centered hide-footer content-class="sp-modal" dialog-class="sp-dialog">
      <template #modal-header>
        <div class="sp-modal-title">
          <span class="sp-modal-title-icon"><b-icon icon="pencil-square"></b-icon></span>
          <div>
            <div class="sp-modal-title-main">
              Edit {{ editItem && editItem.sale_type ? editItem.sale_type : 'promo' }}
            </div>
            <div class="sp-modal-title-sub">{{ editItem ? editItem.sku : '' }}</div>
          </div>
        </div>
      </template>

      <div v-if="editItem" class="sp-modal-body">
        <label class="sp-field">
          <span class="sp-field-label">Product</span>
          <b-form-input :value="editItem.product_name + ' (' + editItem.sku + ')'" readonly class="sp-readonly"></b-form-input>
        </label>

        <label class="sp-field">
          <span class="sp-field-label">Original price</span>
          <b-form-input :value="'$' + formatPrice(editItem.selling_price)" readonly class="sp-readonly"></b-form-input>
        </label>

        <label class="sp-field" v-if="editItem.sale_type !== 'Promo'">
          <span class="sp-field-label">Discount percentage</span>
          <b-input-group size="lg">
            <b-form-input v-model.number="editForm.discountPercentage" type="number" min="0.01" max="100" step="0.01" @input="calculateEditPreview"></b-form-input>
            <b-input-group-append><b-input-group-text class="sp-pct">%</b-input-group-text></b-input-group-append>
          </b-input-group>
        </label>

        <label class="sp-field" v-else>
          <span class="sp-field-label">Discount amount</span>
          <b-input-group size="lg">
            <b-input-group-prepend><b-input-group-text class="sp-pct">$</b-input-group-text></b-input-group-prepend>
            <b-form-input v-model.number="editForm.discountAmount" type="number" min="0.01" step="0.01" @input="calculateEditPreview"></b-form-input>
          </b-input-group>
        </label>

        <label class="sp-field">
          <span class="sp-field-label">Max discount allowed (%)</span>
          <b-input-group size="lg">
            <b-form-input v-model.number="editForm.maxDiscount" type="number" min="0" max="100" step="0.5"></b-form-input>
            <b-input-group-append><b-input-group-text class="sp-pct">%</b-input-group-text></b-input-group-append>
          </b-input-group>
        </label>

        <label class="sp-field">
          <span class="sp-field-label">Start date</span>
          <date-picker v-model="editForm.startDate" format="MM-DD-YYYY" value-type="format" class="sp-datepicker" :clearable="false"></date-picker>
        </label>

        <label class="sp-field">
          <span class="sp-field-label">End date</span>
          <date-picker v-model="editForm.endDate" format="MM-DD-YYYY" value-type="format" class="sp-datepicker" :clearable="false"></date-picker>
        </label>

        <div v-if="editPreview" class="sp-preview-box">
          <strong>Preview:</strong>
          ${{ formatPrice(editItem.selling_price) }} →
          <strong class="sp-price-orange">${{ formatPrice(editPreview) }}</strong>
        </div>

        <div class="sp-modal-foot">
          <b-button class="sp-btn-ghost" size="lg" @click="$bvModal.hide('modal-edit-sale')">Cancel</b-button>
          <b-button class="sp-btn-primary" size="lg" @click="saveEditSale" :disabled="saving">
            <b-spinner v-if="saving" small class="mr-2"></b-spinner>
            Save changes
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment";
import Requestor from "@/utilities/Requestor";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";

export default {
  name: "SalePage",
  components: { DatePicker },
  data() {
    return {
      activeTab: 0,
      packet: {},
      saving: false,

      createMode: "products",
      createForm: {
        actionType: "Sale",
        discountPercentage: null,
        discountAmount: null,
        maxDiscount: 10,
        startDate: moment().format("MM-DD-YYYY"),
        endDate: moment().add(30, "days").format("MM-DD-YYYY"),
      },
      sourceProducts: [],
      loadingSource: false,
      selectedProducts: [],
      selectAllVisible: false,
      filterCategoryId: null,
      filterSubcategoryId: null,
      searchQuery: "",
      subcategories: [],
      preview: [],

      bulkSelectedIds: [],
      bulkSearchQuery: "",

      totalFilteredCount: null,
      currentOffset: 0,
      pageSize: 50,
      hasMore: true,
      loadingMore: false,
      lastScrollTop: 0,

      applyScope: "visible",

      saleItems: [],
      loadingSale: false,
      saleViewFilterCategory: null,
      saleViewSearch: "",

      clearanceItems: [],
      loadingClear: false,
      clearViewFilterCategory: null,
      clearViewSearch: "",

      discountItems: [],
      loadingDisc: false,
      discViewFilterCategory: null,
      discViewSearch: "",

      editItem: null,
      editForm: {
        discountPercentage: null,
        discountAmount: null,
        maxDiscount: null,
        startDate: null,
        endDate: null,
      },
      editPreview: null,
    };
  },

  computed: {
    categoryOptions() {
      if (!this.packet.categories) return [{ value: null, text: "All Categories" }];
      return [{ value: null, text: "All Categories" }, ...this.packet.categories];
    },
    subcategoryOptions() {
      if (!this.filterCategoryId) return [{ value: null, text: "All Subcategories" }];
      const filtered = this.subcategories.filter(s => s.category_id == this.filterCategoryId);
      return [
        { value: null, text: "All Subcategories" },
        ...filtered.map(s => ({ value: s.id, text: s.subcategory })),
      ];
    },
    totalSaleDiscount() {
      return this.saleItems
        .reduce((sum, i) => {
          const d = (parseFloat(i.selling_price) || 0) - (parseFloat(i.special_discount_price) || 0);
          return sum + (d > 0 ? d : 0);
        }, 0)
        .toFixed(2);
    },
    totalClearanceDiscount() {
      return this.clearanceItems
        .reduce((sum, i) => {
          const d = (parseFloat(i.selling_price) || 0) - (parseFloat(i.special_discount_price) || 0);
          return sum + (d > 0 ? d : 0);
        }, 0)
        .toFixed(2);
    },
    totalDiscountAmount() {
      return this.discountItems
        .reduce((sum, i) => {
          const d = (parseFloat(i.selling_price) || 0) - (parseFloat(i.special_discount_price) || 0);
          return sum + (d > 0 ? d : 0);
        }, 0)
        .toFixed(2);
    },
    totalCombinedDiscount() {
      const total =
        (parseFloat(this.totalSaleDiscount) || 0) +
        (parseFloat(this.totalClearanceDiscount) || 0) +
        (parseFloat(this.totalDiscountAmount) || 0);
      return total.toFixed(2);
    },
    totalCombinedDiscountLabel() {
      return "$" + this.totalCombinedDiscount;
    },
    avgSaleDiscount() {
      if (!this.saleItems.length) return 0;
      return (
        this.saleItems.reduce((s, i) => s + this.calculateDiscountPercent(i), 0) /
        this.saleItems.length
      ).toFixed(1);
    },
    avgClearanceDiscount() {
      if (!this.clearanceItems.length) return 0;
      return (
        this.clearanceItems.reduce((s, i) => s + this.calculateDiscountPercent(i), 0) /
        this.clearanceItems.length
      ).toFixed(1);
    },
    avgDiscountAmount() {
      if (!this.discountItems.length) return "0%";
      const avg =
        this.discountItems.reduce((s, i) => s + this.calculateDiscountPercent(i), 0) /
        this.discountItems.length;
      return avg.toFixed(1) + "% eff.";
    },
    avgCombinedDiscount() {
      const total = this.saleItems.length + this.clearanceItems.length + this.discountItems.length;
      if (total === 0) return 0;
      const sum =
        this.saleItems.reduce((s, i) => s + this.calculateDiscountPercent(i), 0) +
        this.clearanceItems.reduce((s, i) => s + this.calculateDiscountPercent(i), 0) +
        this.discountItems.reduce((s, i) => s + this.calculateDiscountPercent(i), 0);
      return (sum / total).toFixed(1);
    },
    filterSummaryText() {
      const parts = [];
      if (this.filterCategoryId) {
        const cat = (this.packet.categories || []).find(c => c.value === this.filterCategoryId);
        parts.push(cat ? cat.text : "Category");
      }
      if (this.filterSubcategoryId) {
        const sub = this.subcategories.find(s => s.id === this.filterSubcategoryId);
        parts.push(sub ? sub.subcategory : "Subcategory");
      }
      if (this.searchQuery) parts.push(`"${this.searchQuery}"`);
      return parts.length ? parts.join(" › ") : "All products";
    },

    filteredCategoriesForBulk() {
      const q = this.bulkSearchQuery.trim().toLowerCase();
      const cats = (this.packet.categories || []).filter(c => c.value !== null);
      if (!q) return cats;
      return cats.filter(c => (c.text || "").toLowerCase().includes(q));
    },
    groupedSubcategoriesForBulk() {
      const q = this.bulkSearchQuery.trim().toLowerCase();
      const subs = this.subcategories.filter(s => {
        if (!q) return true;
        return (s.subcategory || "").toLowerCase().includes(q);
      });
      const byCat = {};
      subs.forEach(s => {
        const cid = s.category_id;
        if (!byCat[cid]) byCat[cid] = [];
        byCat[cid].push(s);
      });
      return Object.keys(byCat).map(cid => {
        const cat = (this.packet.categories || []).find(c => c.value == cid);
        return {
          categoryId: cid,
          categoryName: cat ? cat.text : `Category ${cid}`,
          items: byCat[cid],
        };
      });
    },

    hasValidDiscount() {
      if (this.createForm.actionType === "Promo") {
        return this.createForm.discountAmount > 0 && this.createForm.discountAmount < 100000;
      }
      return this.createForm.discountPercentage > 0 && this.createForm.discountPercentage <= 100;
    },
    isCreateValidComputed() {
      const hasTargets =
        this.createMode === "products"
          ? this.applyScope === "all"
            ? (this.totalFilteredCount || 0) > 0
            : this.selectedProducts.length > 0
          : this.bulkSelectedIds.length > 0;

      const hasDates =
        this.createForm.actionType === "Clearance" ||
        (this.createForm.startDate && this.createForm.endDate);

      return (
        hasTargets &&
        this.hasValidDiscount &&
        this.createForm.maxDiscount >= 0 &&
        this.createForm.maxDiscount <= 100 &&
        hasDates
      );
    },
    submitBarVisibleComputed() {
      if (this.createMode === "products") {
        return this.applyScope === "all"
          ? (this.totalFilteredCount || 0) > 0
          : this.selectedProducts.length > 0;
      }
      return this.bulkSelectedIds.length > 0;
    },

    scopeSummary() {
      if (this.createForm.actionType === "Promo") {
        return `$${parseFloat(this.createForm.discountAmount || 0).toFixed(2)} off`;
      }
      return `${this.createForm.discountPercentage || 0}% ${this.createForm.actionType}`;
    },
    bulkDiscountLabel() {
      if (this.createForm.actionType === "Promo") {
        return `$${parseFloat(this.createForm.discountAmount || 0).toFixed(2)} off`;
      }
      return `${this.createForm.discountPercentage || 0}%`;
    },
    previewPriceColumnLabel() {
      return this.createForm.actionType === "Sale"
        ? "Sale price"
        : this.createForm.actionType === "Clearance"
        ? "Clearance price"
        : "Final price";
    },
    previewPriceClass() {
      if (this.createForm.actionType === "Sale") return "sp-price-teal";
      if (this.createForm.actionType === "Clearance") return "sp-price-danger";
      return "sp-price-orange";
    },
    applyButtonClass() {
      if (this.createForm.actionType === "Sale") return "sp-btn-primary";
      if (this.createForm.actionType === "Clearance") return "sp-btn-danger";
      return "sp-btn-orange";
    },
    applyButtonIcon() {
      if (this.createForm.actionType === "Sale") return "tag-fill";
      if (this.createForm.actionType === "Clearance") return "archive-fill";
      return "cash-coin";
    },
    overriddenCount() {
      return (this.preview || []).filter(p => p.overridden).length;
    },

    configureModalSubtitle() {
      if (this.createMode === "products") {
        return this.applyScope === "all"
          ? `${(this.totalFilteredCount || 0).toLocaleString()} products match the filter`
          : `${this.selectedProducts.length} product(s) selected`;
      }
      const n = this.bulkSelectedIds.length;
      if (this.createMode === "categories") {
        return `${n} categor${n > 1 ? "ies" : "y"} selected — all products inside`;
      }
      return `${n} subcategor${n > 1 ? "ies" : "y"} selected — all products inside`;
    },
    configureScopeTitle() {
      if (this.createMode === "products") {
        return this.applyScope === "all"
          ? "Apply to all filtered products"
          : "Apply to selected products";
      }
      return this.createMode === "categories"
        ? "Apply to whole categories"
        : "Apply to whole subcategories";
    },
    configureScopeSub() {
      if (this.createMode === "products") {
        return this.applyScope === "all"
          ? this.filterSummaryText
          : "Only the products you checked will change";
      }
      return "Every product inside the picked items will change";
    },
  },

  methods: {
    openConfigModal() {
      if (!this.submitBarVisibleComputed) {
        this.$bvToast.toast("Pick products or a bulk scope first.", {
          title: "Nothing to configure",
          variant: "warning",
          autoHideDelay: 2500,
        });
        return;
      }
      this.calculatePreview();
      this.$bvModal.show("modal-configure");
    },
    onConfigureModalHidden() {},
    switchCreateMode(mode) {
      this.createMode = mode;
      this.bulkSelectedIds = [];
      this.bulkSearchQuery = "";
      this.calculatePreview();
    },

    // ============================================
    // FETCH
    // ============================================
    async fetchSourceProducts(reset = true) {
      if (reset) {
        this.sourceProducts = [];
        this.currentOffset = 0;
        this.hasMore = true;
        this.loadingSource = true;
        if (this.$refs.scrollContainer) this.$refs.scrollContainer.scrollTop = 0;
        this.fetchFilteredCount();
      } else {
        this.loadingMore = true;
      }

      try {
        const res = await this.callGet("/api/product/get_products_list_v3/", {
          location_id: this.packet.userDetails.location_id,
          user_role: this.packet.userDetails.userrole,
          user_location_id: this.packet.userDetails.location_id,
          category_id: this.filterCategoryId,
          subcategory_id: this.filterSubcategoryId,
          product_query: this.searchQuery,
          status: "active",
          offset: this.currentOffset,
          limit: this.pageSize,
        });

        if (res.data && res.data.result) {
          const newItems = res.data.result.map(p => ({ ...p, edit: false }));
          this.sourceProducts = reset ? newItems : [...this.sourceProducts, ...newItems];
          this.currentOffset += newItems.length;
          this.hasMore = newItems.length === this.pageSize;
        } else {
          this.hasMore = false;
        }
      } catch (e) {
        console.error("Error fetching source products:", e);
        this.$bvToast.toast("Error loading products", { title: "Error", variant: "danger" });
        this.hasMore = false;
      } finally {
        this.loadingSource = false;
        this.loadingMore = false;
      }
    },

    async fetchFilteredCount() {
      try {
        const res = await this.callGet("/api/product/count_filtered_products/", {
          category_id: this.filterCategoryId,
          subcategory_id: this.filterSubcategoryId,
          product_query: this.searchQuery,
          status: "active",
        });
        if (res.data && !res.data.error) {
          this.totalFilteredCount = res.data.result.count;
        }
      } catch (e) {
        this.totalFilteredCount = null;
      }
    },

    onFilterChange() {
      this.selectedProducts = [];
      this.selectAllVisible = false;
      this.applyScope = "visible";
      this.fetchSourceProducts(true);
    },

    onTableScroll(event) {
      const el = event.target;
      if (el.scrollTop === this.lastScrollTop) return;
      this.lastScrollTop = el.scrollTop;
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
      if (nearBottom && !this.loadingMore && this.hasMore) {
        this.fetchSourceProducts(false);
      }
    },

    setScope(scope) {
      this.applyScope = scope;
      if (scope === "all") {
        this.selectedProducts = [];
        this.selectAllVisible = false;
      }
      this.calculatePreview();
    },

    toggleProduct(sku) {
      const i = this.selectedProducts.indexOf(sku);
      if (i === -1) this.selectedProducts.push(sku);
      else this.selectedProducts.splice(i, 1);
      this.calculatePreview();
    },

    toggleSelectAllVisible(checked) {
      if (checked) {
        const visible = this.sourceProducts.map(p => p.sku);
        this.selectedProducts = Array.from(new Set([...this.selectedProducts, ...visible]));
      } else {
        const visibleSet = new Set(this.sourceProducts.map(p => p.sku));
        this.selectedProducts = this.selectedProducts.filter(s => !visibleSet.has(s));
      }
      this.calculatePreview();
    },

    clearSelection() {
      this.selectedProducts = [];
      this.selectAllVisible = false;
      this.preview = [];
    },

    toggleBulkId(id) {
      const i = this.bulkSelectedIds.indexOf(id);
      if (i === -1) this.bulkSelectedIds.push(id);
      else this.bulkSelectedIds.splice(i, 1);
    },

    // ============================================
    // PREVIEW  (now with per-item overrides)
    // ============================================
    calculatePreview() {
      const isPct = this.createForm.actionType !== "Promo";
      const globalValue = isPct
        ? parseFloat(this.createForm.discountPercentage)
        : parseFloat(this.createForm.discountAmount);

      // Snapshot existing per-row overrides by SKU
      const overrides = {};
      (this.preview || []).forEach(p => {
        if (p.overridden) {
          overrides[p.sku] = { percent: p.percent, amount: p.amount };
        }
      });

      // If global is empty but rows are customised, keep them
      if (!globalValue || globalValue <= 0) {
        if (this.preview.length) {
          this.preview = this.preview.map(row => {
            const ov = overrides[row.sku];
            if (!ov) return row;
            return this._recomputeFromRow({ ...row, ...ov });
          });
        } else {
          this.preview = [];
        }
        return;
      }

      let source = [];
      if (this.applyScope === "all") {
        source = this.sourceProducts.slice(0, 5);
      } else {
        const selectedSet = new Set(this.selectedProducts);
        source = this.sourceProducts.filter(p => selectedSet.has(p.sku)).slice(0, 5);
      }

      this.preview = source.map(p => {
        const original = parseFloat(p.selling_price) || 0;
        const ov = overrides[p.sku];

        let percent, amount;
        if (isPct) {
          percent = ov ? ov.percent : globalValue;
          amount = Math.round(original * (percent / 100) * 100) / 100;
        } else {
          amount = ov ? ov.amount : globalValue;
          percent = original > 0 ? Math.round((amount / original) * 100 * 100) / 100 : 0;
        }

        const salePrice = Math.max(0, Math.round((original - amount) * 100) / 100);

        return {
          sku: p.sku,
          original,
          percent,
          amount,
          priceOff: amount,
          salePrice,
          overridden: !!ov,
        };
      });
    },

    _recomputeFromRow(row) {
      const original = row.original;
      const isPct = this.createForm.actionType !== "Promo";
      let percent = row.percent;
      let amount = row.amount;

      if (isPct) {
        amount = Math.round(original * (percent / 100) * 100) / 100;
      } else {
        percent = original > 0 ? Math.round((amount / original) * 100 * 100) / 100 : 0;
      }
      const salePrice = Math.max(0, Math.round((original - amount) * 100) / 100);
      return { ...row, percent, amount, priceOff: amount, salePrice, overridden: true };
    },

    onPreviewRowChange(row) {
      row.overridden = true;
      const isPct = this.createForm.actionType !== "Promo";
      if (isPct) {
        row.percent = Math.max(0, Math.min(100, parseFloat(row.percent) || 0));
        row.amount = Math.round(row.original * (row.percent / 100) * 100) / 100;
      } else {
        row.amount = Math.max(0, parseFloat(row.amount) || 0);
        row.percent = row.original > 0
          ? Math.round((row.amount / row.original) * 100 * 100) / 100
          : 0;
      }
      row.priceOff = row.amount;
      row.salePrice = Math.max(0, Math.round((row.original - row.amount) * 100) / 100);
    },

    resetPreviewRow(row) {
      row.overridden = false;
      const isPct = this.createForm.actionType !== "Promo";
      if (isPct) {
        row.percent = parseFloat(this.createForm.discountPercentage) || 0;
        row.amount = Math.round(row.original * (row.percent / 100) * 100) / 100;
      } else {
        row.amount = parseFloat(this.createForm.discountAmount) || 0;
        row.percent = row.original > 0
          ? Math.round((row.amount / row.original) * 100 * 100) / 100
          : 0;
      }
      row.priceOff = row.amount;
      row.salePrice = Math.max(0, Math.round((row.original - row.amount) * 100) / 100);
    },

    resetCreateForm() {
      this.selectedProducts = [];
      this.selectAllVisible = false;
      this.applyScope = "visible";
      this.bulkSelectedIds = [];
      this.bulkSearchQuery = "";
      this.createForm = {
        actionType: "Sale",
        discountPercentage: null,
        discountAmount: null,
        maxDiscount: 10,
        startDate: moment().format("MM-DD-YYYY"),
        endDate: moment().add(30, "days").format("MM-DD-YYYY"),
      };
      this.preview = [];
    },

    // ============================================
    // BUILD PAYLOAD
    // ============================================
    buildDiscountPayload() {
      const payload = {
        sale_type: this.createForm.actionType,
        max_discount_percentage: parseFloat(this.createForm.maxDiscount),
        user_details: {
          username: this.packet.userDetails.username,
          user_id: this.packet.userDetails.user_id,
        },
      };

      if (this.createForm.actionType === "Promo") {
        payload.discount_amount = parseFloat(this.createForm.discountAmount);
        payload.discount_mode = "amount";
      } else {
        payload.discount_percentage = parseFloat(this.createForm.discountPercentage);
        payload.discount_mode = "percentage";
      }

      if (this.createForm.actionType !== "Clearance" && this.createForm.endDate) {
        payload.discount_end_date = this.createForm.endDate;
      }

      if (this.createForm.actionType !== "Clearance" && this.createForm.startDate) {
        payload.discount_start_date = this.createForm.startDate;
      }

      return payload;
    },

    // ============================================
    // APPLY PER-ITEM OVERRIDES (separate API calls)
    // ============================================
    async applyPerItemOverrides() {
      const overridden = (this.preview || []).filter(p => p.overridden);
      if (!overridden.length) return { count: 0 };

      const promises = overridden.map(p => {
        const payload = {
          product_skus: [p.sku],
          sale_type: this.createForm.actionType,
          max_discount_percentage: parseFloat(this.createForm.maxDiscount),
          user_details: {
            username: this.packet.userDetails.username,
            user_id: this.packet.userDetails.user_id,
          },
        };

        if (this.createForm.actionType === "Promo") {
          payload.discount_amount = p.amount;
          payload.discount_mode = "amount";
        } else {
          payload.discount_percentage = p.percent;
          payload.discount_mode = "percentage";
        }

        if (this.createForm.actionType !== "Clearance" && this.createForm.endDate) {
          payload.discount_end_date = this.createForm.endDate;
        }
        if (this.createForm.actionType !== "Clearance" && this.createForm.startDate) {
          payload.discount_start_date = this.createForm.startDate;
        }

        return this.callPost("/api/product/change_bulk_product_status/", payload);
      });

      const results = await Promise.all(promises);
      const errors = results.filter(r => r && r.data && r.data.error);
      if (errors.length) {
        console.warn("Some per-item overrides failed:", errors);
      }
      return { count: overridden.length, errors: errors.length };
    },

    // ============================================
    // EXECUTE CREATE
    // ============================================
    async executeCreate() {
      if (!this.isCreateValidComputed) {
        this.$bvToast.toast("Please complete all fields correctly", {
          title: "Validation",
          variant: "warning",
        });
        return;
      }

      // ----- BULK MODE -----
      if (this.createMode !== "products") {
        const label =
          this.createMode === "categories"
            ? this.bulkSelectedIds.length + " categor" + (this.bulkSelectedIds.length > 1 ? "ies" : "y")
            : this.bulkSelectedIds.length + " subcategor" + (this.bulkSelectedIds.length > 1 ? "ies" : "y");

        const ok = confirm(
          `You are about to apply ${this.bulkDiscountLabel} ${this.createForm.actionType} ` +
            `to ALL products inside ${label}.\n\nThis could affect thousands of products. Continue?`
        );
        if (!ok) return;

        this.saving = true;
        try {
          const payload = this.buildDiscountPayload();
          payload.update_children = true;

          if (this.createMode === "categories") {
            payload.apply_to_categories = true;
            payload.category_ids = this.bulkSelectedIds;
          } else {
            payload.apply_to_subcategories = true;
            payload.subcategory_ids = this.bulkSelectedIds;
          }

          const res = await this.callPost("/api/product/change_bulk_product_status/", payload);
          if (res.data.error) throw new Error(res.data.error);

          const updated = (res.data.result && res.data.result.updated_count) || "?";
          this.$bvToast.toast(
            `✅ Applied ${this.bulkDiscountLabel} ${this.createForm.actionType} to ${updated} product(s)`,
            { title: "Success", variant: "success", autoHideDelay: 4000 }
          );

          const goToTab =
            this.createForm.actionType === "Sale" ? 1
            : this.createForm.actionType === "Clearance" ? 2 : 3;

          this.$bvModal.hide("modal-configure");
          this.resetCreateForm();
          this.fetchSourceProducts(true);
          this.fetchSaleItems();
          this.fetchClearanceItems();
          this.fetchDiscountItems();
          this.activeTab = goToTab;
        } catch (e) {
          console.error("Error applying bulk sale:", e);
          this.$bvToast.toast(e.message || "Error applying sale", {
            title: "Error", variant: "danger",
          });
        } finally {
          this.saving = false;
        }
        return;
      }

      // ----- PRODUCTS MODE -----
      const isAllScope = this.applyScope === "all";
      const itemCount = isAllScope ? this.totalFilteredCount || 0 : this.selectedProducts.length;

      if (isAllScope && itemCount > 100) {
        const ok = confirm(
          `You are about to apply ${this.scopeSummary} ` +
            `to ALL ${itemCount.toLocaleString()} products matching the current filter.\n\nContinue?`
        );
        if (!ok) return;
      }

      this.saving = true;
      try {
        const payload = this.buildDiscountPayload();
        payload.update_children = false;

        if (isAllScope) {
          payload.apply_to_all = true;
          payload.filter = {
            category_id: this.filterCategoryId,
            subcategory_id: this.filterSubcategoryId,
            product_query: this.searchQuery,
            status: "active",
          };
        } else {
          payload.product_skus = this.selectedProducts;
        }

        const res = await this.callPost("/api/product/change_bulk_product_status/", payload);
        if (res.data.error) throw new Error(res.data.error);

        // Then apply any per-item overrides on top
        const overrideResult = await this.applyPerItemOverrides();

        const updated = (res.data.result && res.data.result.updated_count) || itemCount;
        const overrideMsg = overrideResult.count
          ? ` (${overrideResult.count} row(s) customised)`
          : "";
        this.$bvToast.toast(
          `✅ Applied ${this.scopeSummary} to ${updated} product(s)${overrideMsg}`,
          { title: "Success", variant: "success", autoHideDelay: 4000 }
        );

        const goToTab =
          this.createForm.actionType === "Sale" ? 1
          : this.createForm.actionType === "Clearance" ? 2 : 3;

        this.$bvModal.hide("modal-configure");
        this.resetCreateForm();
        this.fetchSourceProducts(true);
        this.fetchSaleItems();
        this.fetchClearanceItems();
        this.fetchDiscountItems();
        this.activeTab = goToTab;
      } catch (e) {
        console.error("Error applying sale:", e);
        this.$bvToast.toast(e.message || "Error applying sale", {
          title: "Error", variant: "danger",
        });
      } finally {
        this.saving = false;
      }
    },

    // ============================================
    // FETCHERS
    // ============================================
    async fetchSaleItems() {
      this.loadingSale = true;
      try {
        const res = await this.callGet("/api/product/get_discounted_products/", {
          discount_type: "active",
          sale_type_filter: "Sale",
          location_id: this.packet.userDetails.location_id,
          user_role: this.packet.userDetails.userrole,
          user_location_id: this.packet.userDetails.location_id,
          category_id: this.saleViewFilterCategory,
          product_query: this.saleViewSearch,
          limit: 1000,
        });
        if (res.data && res.data.data) {
          this.saleItems = res.data.data.filter(i => i.sale_type === "Sale");
        } else {
          this.saleItems = [];
        }
      } catch (e) {
        console.error("Error fetching sale items:", e);
        this.saleItems = [];
      } finally {
        this.loadingSale = false;
      }
    },

    async fetchClearanceItems() {
      this.loadingClear = true;
      try {
        const res = await this.callGet("/api/product/get_discounted_products/", {
          discount_type: "active",
          sale_type_filter: "Clearance",
          location_id: this.packet.userDetails.location_id,
          user_role: this.packet.userDetails.userrole,
          user_location_id: this.packet.userDetails.location_id,
          category_id: this.clearViewFilterCategory,
          product_query: this.clearViewSearch,
          limit: 1000,
        });
        if (res.data && res.data.data) {
          this.clearanceItems = res.data.data.filter(i => i.sale_type === "Clearance");
        } else {
          this.clearanceItems = [];
        }
      } catch (e) {
        console.error("Error fetching clearance items:", e);
        this.clearanceItems = [];
      } finally {
        this.loadingClear = false;
      }
    },

    async fetchDiscountItems() {
      this.loadingDisc = true;
      try {
        const res = await this.callGet("/api/product/get_discounted_products/", {
          discount_type: "active",
          sale_type_filter: "Promo",
          location_id: this.packet.userDetails.location_id,
          user_role: this.packet.userDetails.userrole,
          user_location_id: this.packet.userDetails.location_id,
          category_id: this.discViewFilterCategory,
          product_query: this.discViewSearch,
          limit: 1000,
        });
        if (res.data && res.data.data) {
          this.discountItems = res.data.data.filter(i => i.sale_type === "Promo");
        } else {
          this.discountItems = [];
        }
      } catch (e) {
        console.error("Error fetching promo items:", e);
        this.discountItems = [];
      } finally {
        this.loadingDisc = false;
      }
    },

    // ============================================
    // EDIT / REMOVE
    // ============================================
    editItemSale(item) {
      this.editItem = item;
      const isAmountMode = item.sale_type === "Promo";
      this.editForm = {
        discountPercentage: isAmountMode ? null : this.calculateDiscountPercent(item),
        discountAmount: isAmountMode ? this.calculateDiscountAmount(item) : null,
        maxDiscount: item.max_discount_percentage || 0,
        startDate: item.special_discount_start_date,
        endDate: item.special_discount_end_date,
      };
      this.editPreview = null;
      this.$bvModal.show("modal-edit-sale");
    },

    calculateEditPreview() {
      if (!this.editItem) {
        this.editPreview = null;
        return;
      }
      const original = parseFloat(this.editItem.selling_price) || 0;
      if (this.editItem.sale_type === "Promo") {
        const amt = parseFloat(this.editForm.discountAmount) || 0;
        if (amt <= 0) {
          this.editPreview = null;
          return;
        }
        this.editPreview = Math.max(0, Math.round((original - amt) * 100) / 100);
      } else {
        const pct = parseFloat(this.editForm.discountPercentage);
        if (!pct || pct <= 0) {
          this.editPreview = null;
          return;
        }
        this.editPreview = Math.round(original * (1 - pct / 100) * 100) / 100;
      }
    },

    async saveEditSale() {
      if (!this.editItem) return;
      this.saving = true;
      try {
        const payload = {
          product_skus: [this.editItem.sku],
          sale_type: this.editItem.sale_type || "Sale",
          max_discount_percentage: parseFloat(this.editForm.maxDiscount),
          discount_start_date: this.editForm.startDate,
          discount_end_date: this.editForm.endDate,
          user_details: {
            username: this.packet.userDetails.username,
            user_id: this.packet.userDetails.user_id,
          },
        };

        if (this.editItem.sale_type === "Promo") {
          payload.discount_amount = parseFloat(this.editForm.discountAmount);
          payload.discount_mode = "amount";
        } else {
          payload.discount_percentage = parseFloat(this.editForm.discountPercentage);
          payload.discount_mode = "percentage";
        }

        const res = await this.callPost("/api/product/change_bulk_product_status/", payload);
        if (res.data.error) throw new Error(res.data.error);
        this.$bvToast.toast(`Updated ${this.editItem.sku}`, { title: "Success", variant: "success" });
        this.$bvModal.hide("modal-edit-sale");
        this.editItem = null;
        this.fetchSaleItems();
        this.fetchClearanceItems();
        this.fetchDiscountItems();
      } catch (e) {
        this.$bvToast.toast(e.message || "Error updating", { title: "Error", variant: "danger" });
      } finally {
        this.saving = false;
      }
    },

    async removeSale(item) {
      if (!confirm(`Remove ${item.sale_type || "sale"} from ${item.sku}?`)) return;
      try {
        const res = await this.callPost("/api/product/change_bulk_product_status/", {
          product_skus: [item.sku],
          sale_type: null,
          user_details: {
            username: this.packet.userDetails.username,
            user_id: this.packet.userDetails.user_id,
          },
        });
        if (res.data.error) throw new Error(res.data.error);
        this.$bvToast.toast(`Removed ${item.sku}`, { title: "Success", variant: "success" });
        this.fetchSaleItems();
        this.fetchClearanceItems();
        this.fetchDiscountItems();
        this.fetchSourceProducts(true);
      } catch (e) {
        this.$bvToast.toast(e.message || "Error removing", { title: "Error", variant: "danger" });
      }
    },

    // ============================================
    // HELPERS
    // ============================================
    callGet(path, data) {
      return new Promise((resolve, reject) => {
        Requestor.getSyncP(path, data, this, resolve, reject);
      });
    },
    callPost(path, data) {
      return new Promise((resolve, reject) => {
        Requestor.postSyncP(path, data, this, resolve, reject);
      });
    },
    formatPrice(p) {
      if (!p && p !== 0) return "0.00";
      const n = parseFloat(p);
      return isNaN(n) ? "0.00" : n.toFixed(2);
    },
    formatDate(d) {
      return d ? moment(d).format("MMM DD, YYYY") : "—";
    },
    isExpired(item) {
      if (!item.special_discount_end_date) return false;
      return moment(item.special_discount_end_date).endOf("day").isBefore(moment());
    },
    isScheduled(item) {
      if (!item.special_discount_start_date) return false;
      return moment(item.special_discount_start_date).startOf("day").isAfter(moment());
    },
    calculateDiscountPercent(item) {
      const orig = parseFloat(item.selling_price);
      const sale = parseFloat(item.special_discount_price);
      if (!orig || orig <= 0 || !sale) return 0;
      return Math.round(((orig - sale) / orig) * 100 * 10) / 10;
    },
    calculateDiscountAmount(item) {
      const orig = parseFloat(item.selling_price) || 0;
      const sale = parseFloat(item.special_discount_price) || 0;
      const diff = orig - sale;
      return diff > 0 ? diff : 0;
    },
    getProductImage(item) {
      if (!item || !item.image_url) return null;
      let url = item.image_url.trim();
      if (!url || url === "null" || url === "undefined") return null;
      if (url.includes("fmapi.myfurnituremecca.com")) {
        return `/api/image/image-proxy/?url=${encodeURIComponent(url)}`;
      }
      return url;
    },
    getSubcategoryName(item) {
      if (!item.subcategory_id) return "";
      const sub = this.subcategories.find(s => s.id === item.subcategory_id);
      return sub ? sub.subcategory : "";
    },
    handleImageError(e) {
      e.target.style.display = "none";
    },
  },

  async fetchSubcategories() {
    try {
      const res = await this.callGet("/api/product/get_subcategories/", {});
      if (res.data && !res.data.error) this.subcategories = res.data.result || [];
    } catch (e) {
      /* silent */
    }
  },

  created() {
    this.packet = JSON.parse(localStorage.packet || "{}");
    this.packet.userDetails = JSON.parse(localStorage.userDetails || "{}");
    if (!this.packet.categories) this.packet.categories = [];

    this.fetchSubcategories();
    this.fetchSourceProducts(true);
    this.fetchSaleItems();
    this.fetchClearanceItems();
    this.fetchDiscountItems();
  },

  watch: {
    activeTab(newVal) {
      if (newVal === 1) this.fetchSaleItems();
      if (newVal === 2) this.fetchClearanceItems();
      if (newVal === 3) this.fetchDiscountItems();
      if (newVal === 0) this.fetchSourceProducts(true);
    },
    "createForm.actionType"() {
      this.calculatePreview();
    },
  },
};
</script>

<style scoped>
/* ============================================ */
/* PAGE SHELL                                    */
/* ============================================ */
.sp-page {
  position: relative;
  background: #F6F4F0;
  min-height: 100vh;
  padding: 18px 0 60px;
  font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: #221F1C;
  font-size: 15px;
}
.sp-page > *:not(.sp-modal) {
  max-width: 1480px;
  width: 94%;
  margin-left: auto;
  margin-right: auto;
}
@media (max-width: 767px) {
  .sp-page { padding: 10px 0 40px; }
  .sp-page > *:not(.sp-modal) { width: 100%; padding: 0 12px; }
}

/* ============================================ */
/* HERO                                          */
/* ============================================ */
.sp-hero {
  background: #221F1C;
  color: #fff;
  border-radius: 20px;
  overflow: hidden;
  border-bottom: 5px solid #EE7A22;
  margin-bottom: 18px;
}
.sp-hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(0, 1fr);
  gap: 28px;
  padding: 26px 30px;
  align-items: center;
}
.sp-hero-title {
  font-family: 'Sora', sans-serif;
  font-size: 38px;
  font-weight: 800;
  line-height: 1.1;
  margin: 4px 0 12px;
}
.sp-hero-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px; }
.sp-tag {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12.5px; font-weight: 700;
  padding: 4px 12px; border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.14);
}
.sp-tag.is-orange { background: rgba(238, 122, 34, 0.22); color: #FFB06B; border-color: rgba(238, 122, 34, 0.45); }
.sp-tag.is-teal { background: rgba(15, 110, 107, 0.24); color: #7FDACF; border-color: rgba(15, 110, 107, 0.5); }
.sp-tag.is-danger { background: rgba(201, 60, 48, 0.24); color: #FFB0A5; border-color: rgba(201, 60, 48, 0.5); }
.sp-hero-qr {
  display: flex; align-items: center; gap: 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  padding: 16px 20px;
  color: #FFB06B;
}
.sp-qr-title { font-family: 'Sora', sans-serif; font-weight: 700; font-size: 15px; color: #fff; }
.sp-qr-sub { font-size: 13px; color: rgba(255, 255, 255, 0.6); }
@media (max-width: 900px) {
  .sp-hero-grid { grid-template-columns: 1fr; gap: 18px; }
  .sp-hero-title { font-size: 28px; }
}

/* ============================================ */
/* STATS CARDS                                   */
/* ============================================ */
.sp-stats {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}
.sp-stat {
  display: flex; align-items: center; gap: 14px;
  background: #fff;
  border: 1px solid #E5E1DA;
  border-left: 5px solid transparent;
  border-radius: 16px;
  padding: 16px 18px;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}
.sp-stat:hover { transform: translateY(-2px); box-shadow: 0 10px 24px -16px rgba(34, 31, 28, 0.35); }
.sp-stat-icon {
  width: 48px; height: 48px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 20px; flex-shrink: 0;
}
.sp-stat-body { display: flex; flex-direction: column; min-width: 0; }
.sp-stat-value {
  font-family: 'Sora', sans-serif;
  font-size: 24px; font-weight: 800;
  line-height: 1.1; letter-spacing: -0.01em;
}
.sp-stat-label { font-size: 12.5px; color: #736C63; font-weight: 600; margin-top: 2px; }

.sp-stat.is-sale   { border-left-color: #0F6E6B; }
.sp-stat.is-sale   .sp-stat-icon { background: linear-gradient(135deg, #0F6E6B, #0B5552); }
.sp-stat.is-sale   .sp-stat-value { color: #0F6E6B; }
.sp-stat.is-clear  { border-left-color: #C93C30; }
.sp-stat.is-clear  .sp-stat-icon { background: linear-gradient(135deg, #C93C30, #A22A1F); }
.sp-stat.is-clear  .sp-stat-value { color: #C93C30; }
.sp-stat.is-disc   { border-left-color: #D4620F; }
.sp-stat.is-disc   .sp-stat-icon { background: linear-gradient(135deg, #EE7A22, #D4620F); }
.sp-stat.is-disc   .sp-stat-value { color: #D4620F; }
.sp-stat.is-total  { border-left-color: #2B83B5; }
.sp-stat.is-total  .sp-stat-icon { background: linear-gradient(135deg, #2B83B5, #1E6690); }
.sp-stat.is-total  .sp-stat-value { color: #2B83B5; }
.sp-stat.is-avg    { border-left-color: #C98A12; }
.sp-stat.is-avg    .sp-stat-icon { background: linear-gradient(135deg, #E0A524, #C98A12); }
.sp-stat.is-avg    .sp-stat-value { color: #C98A12; }

@media (max-width: 1199px) { .sp-stats { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (max-width: 767px)  { .sp-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; } }
@media (max-width: 460px)  { .sp-stats { grid-template-columns: 1fr; } }

/* ============================================ */
/* TABS                                          */
/* ============================================ */
.sp-tabs {
  position: sticky; top: 0; z-index: 20;
  display: flex; gap: 8px; overflow-x: auto;
  padding: 10px; margin-bottom: 18px;
  background: rgba(255, 255, 255, 0.86);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid #E5E1DA;
  border-radius: 99px;
  scrollbar-width: thin;
}
.sp-tab {
  flex: 0 0 auto;
  display: inline-flex; align-items: center; gap: 10px;
  border: 1px solid #E5E1DA;
  background: #fff; color: #221F1C;
  padding: 8px 16px 8px 8px;
  border-radius: 99px;
  font-size: 14.5px; font-weight: 700;
  cursor: pointer; white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.sp-tab:hover { border-color: #EE7A22; }
.sp-tab:focus-visible { outline: 2px solid #EE7A22; outline-offset: 2px; }
.sp-tab-icon {
  width: 28px; height: 28px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 13px; background: #F1EEE9; color: #736C63;
}
.sp-tab.is-active {
  background: #EE7A22; border-color: #EE7A22; color: #fff;
  box-shadow: 0 8px 18px -8px rgba(238, 122, 34, 0.8);
}
.sp-tab.is-active .sp-tab-icon { background: #fff; color: #D4620F; }
.sp-tab.is-sale.is-active   { background: #0F6E6B; border-color: #0F6E6B; box-shadow: 0 8px 18px -8px rgba(15, 110, 107, 0.8); }
.sp-tab.is-sale.is-active   .sp-tab-icon { background: #fff; color: #0F6E6B; }
.sp-tab.is-clear.is-active  { background: #C93C30; border-color: #C93C30; box-shadow: 0 8px 18px -8px rgba(201, 60, 48, 0.8); }
.sp-tab.is-clear.is-active  .sp-tab-icon { background: #fff; color: #C93C30; }
.sp-tab.is-disc.is-active   { background: #D4620F; border-color: #D4620F; box-shadow: 0 8px 18px -8px rgba(212, 98, 15, 0.8); }
.sp-tab.is-disc.is-active   .sp-tab-icon { background: #fff; color: #D4620F; }
.sp-tab-count {
  background: #FFF1E4; color: #D4620F;
  border-radius: 99px;
  font-size: 12px; padding: 2px 9px; font-weight: 800;
}
.sp-tab.is-active .sp-tab-count { background: rgba(255, 255, 255, 0.22); color: #fff; }
.sp-tab.is-sale.is-active .sp-tab-count { background: rgba(255, 255, 255, 0.22); color: #fff; }
.sp-tab.is-clear.is-active .sp-tab-count { background: rgba(255, 255, 255, 0.22); color: #fff; }
.sp-tab.is-disc.is-active .sp-tab-count { background: rgba(255, 255, 255, 0.22); color: #fff; }

/* ============================================ */
/* PANEL / CARD                                  */
/* ============================================ */
.sp-panel { display: block; }
.sp-card {
  background: #fff;
  border: 1px solid #E5E1DA;
  border-radius: 18px;
  padding: 22px 24px;
  margin-bottom: 18px;
}

/* ============================================ */
/* MODE TABS                                     */
/* ============================================ */
.sp-mode-tabs {
  display: inline-flex;
  background: #F1EEE9;
  border-radius: 12px;
  padding: 5px; gap: 4px;
  margin-bottom: 16px;
}
.sp-mode-tab {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 20px;
  border: none; border-radius: 8px;
  background: transparent; color: #736C63;
  font-weight: 700; font-size: 14px; cursor: pointer;
  transition: all 0.18s ease;
}
.sp-mode-tab:hover { color: #221F1C; background: #E5E1DA; }
.sp-mode-tab.is-active {
  background: #fff; color: #D4620F;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

/* ============================================ */
/* TOOLBAR                                       */
/* ============================================ */
.sp-toolbar {
  display: flex; align-items: center; gap: 12px;
  background: #F7F5F2;
  padding: 10px 14px;
  border: 1px solid #E5E1DA;
  border-radius: 14px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}
.sp-segment {
  display: inline-flex;
  background: #fff;
  border-radius: 10px;
  padding: 4px; gap: 2px;
  border: 1px solid #E5E1DA;
  flex-shrink: 0;
}
.sp-seg-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 14px;
  border: none; border-radius: 8px;
  background: transparent; color: #736C63;
  font-weight: 700; font-size: 13.5px;
  cursor: pointer; transition: all 0.15s ease;
  white-space: nowrap;
}
.sp-seg-btn:hover { background: #F1EEE9; color: #221F1C; }
.sp-seg-btn.is-active { background: #EE7A22; color: #fff; }
.sp-seg-btn.is-active:first-child { background: #0F6E6B; color: #fff; }
.sp-seg-btn.is-active:last-child  { background: #C93C30; color: #fff; }
.sp-seg-btn:nth-child(3).is-active { background: #D4620F; }

.sp-vdivider { width: 1px; height: 32px; background: #E5E1DA; flex-shrink: 0; }

.sp-filter {
  width: 180px !important;
  min-width: 180px;
  height: 40px;
  font-size: 14px;
  border-radius: 10px;
  border: 1.5px solid #E5E1DA;
  background: #fff;
}
.sp-search {
  flex: 1 1 auto;
  min-width: 220px;
  max-width: 420px;
}
.sp-search >>> .form-control {
  height: 40px; font-size: 14px;
  border-radius: 10px 0 0 10px;
  border: 1.5px solid #E5E1DA;
  background: #fff;
}
.sp-search >>> .btn { height: 40px; border-radius: 0 10px 10px 0; }

.sp-chip {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 700;
  padding: 4px 11px; border-radius: 999px;
  background: #F1EEE9; color: #4A4540;
  border: 1px solid #E5E1DA;
  white-space: nowrap;
}
.sp-chip.is-selected { background: #FFF1E4; color: #D4620F; border-color: #F5CFAE; }
.sp-chip.is-sale     { background: #E2F2F0; color: #0F6E6B; border-color: #BFE1DD; }
.sp-chip.is-clear    { background: #FBE9E7; color: #C93C30; border-color: #F3C9C3; }
.sp-chip.is-disc     { background: #FFF1E4; color: #D4620F; border-color: #F5CFAE; }
.sp-chip.is-warn     { background: #FFF4DC; color: #8A5E09; border-color: #F2DBA5; }
.sp-chip.is-discount-tag { background: #FBE9E7; color: #C93C30; border-color: #F3C9C3; }
.sp-chip-x { cursor: pointer; margin-left: 2px; transition: transform 0.15s ease; }
.sp-chip-x:hover { transform: scale(1.15); }

/* ============================================ */
/* BUTTONS                                       */
/* ============================================ */
.sp-btn-primary.btn,
.sp-btn-danger.btn,
.sp-btn-orange.btn,
.sp-btn-ghost.btn,
.sp-btn-ghost-dark.btn {
  border-radius: 10px !important;
  font-weight: 700; font-size: 14px;
  padding: 8px 16px;
  display: inline-flex; align-items: center; gap: 7px;
  box-shadow: none !important;
}
.sp-btn-primary.btn { background: #0F6E6B !important; border: 1.5px solid #0F6E6B !important; color: #fff !important; }
.sp-btn-primary.btn:hover:not(:disabled) { background: #0B5552 !important; border-color: #0B5552 !important; }
.sp-btn-danger.btn { background: #C93C30 !important; border: 1.5px solid #C93C30 !important; color: #fff !important; }
.sp-btn-danger.btn:hover:not(:disabled) { background: #A22A1F !important; border-color: #A22A1F !important; }
.sp-btn-orange.btn { background: #EE7A22 !important; border: 1.5px solid #EE7A22 !important; color: #fff !important; }
.sp-btn-orange.btn:hover:not(:disabled) { background: #D4620F !important; border-color: #D4620F !important; }
.sp-btn-ghost.btn { background: #fff !important; color: #221F1C !important; border: 1.5px solid #E5E1DA !important; }
.sp-btn-ghost.btn:hover:not(:disabled) { border-color: #EE7A22 !important; color: #D4620F !important; }
.sp-btn-ghost.btn.is-danger { color: #C93C30 !important; }
.sp-btn-ghost.btn.is-danger:hover { border-color: #C93C30 !important; }
.sp-btn-ghost-dark.btn { background: rgba(255, 255, 255, 0.06) !important; color: #fff !important; border: 1.5px solid rgba(255, 255, 255, 0.22) !important; }
.sp-btn-ghost-dark.btn:hover:not(:disabled) { border-color: #EE7A22 !important; }
.sp-btn-icon.btn { padding: 6px 10px !important; }
.sp-page .btn:disabled { opacity: 0.5; }

/* ============================================ */
/* SCOPE BANNER                                  */
/* ============================================ */
.sp-scope {
  display: flex; justify-content: space-between; align-items: center; gap: 12px;
  background: #E7F0F7; border: 1px solid #BBD5E8;
  border-radius: 12px; padding: 12px 16px;
  margin-bottom: 14px; flex-wrap: wrap;
}
.sp-scope-left { display: flex; align-items: center; gap: 12px; color: #234A66; font-size: 14px; }
.sp-scope-icon { font-size: 20px; color: #2B83B5; flex-shrink: 0; }
.sp-scope-left strong { font-size: 14px; }
.sp-scope-sub { font-size: 12.5px; color: #4A6A82; margin-top: 2px; }
.sp-scope-right { display: flex; gap: 8px; flex-wrap: wrap; }

/* ============================================ */
/* TABLES                                        */
/* ============================================ */
.sp-table-wrap {
  max-height: 520px; overflow: auto;
  border: 1px solid #E5E1DA; border-radius: 14px;
  background: #fff;
}
.sp-table {
  width: 100%; border-collapse: collapse; font-size: 14px;
}
.sp-table thead th {
  position: sticky; top: 0;
  background: #F7F5F2;
  padding: 12px 10px;
  text-align: left;
  font-size: 11px;
  text-transform: uppercase;
  font-weight: 800; letter-spacing: 0.05em;
  color: #736C63;
  border-bottom: 2px solid #E5E1DA;
  z-index: 2; white-space: nowrap;
}
.sp-table td {
  padding: 10px;
  border-bottom: 1px solid #F1EEE9;
  vertical-align: middle;
  color: #221F1C;
}
.sp-table tbody tr:hover { background: #FAF9F7; }
.sp-row { cursor: pointer; }
.sp-row.is-selected { background: #FFF1E4 !important; }

.col-check { width: 42px; text-align: center; }
.col-img { width: 62px; }
.col-sku { width: 150px; }
.col-name { min-width: 200px; }
.col-cat { width: 200px; }
.col-price { width: 110px; }
.col-status { width: 130px; }
.col-disc { width: 100px; text-align: center; }
.col-sale { width: 120px; }
.col-max { width: 100px; text-align: center; }
.col-end { width: 140px; }
.col-act { width: 110px; white-space: nowrap; }

.sp-sku {
  font-family: 'Menlo', 'Consolas', monospace;
  font-size: 12.5px; font-weight: 700;
  color: #D4620F; background: #FFF1E4;
  padding: 3px 8px; border-radius: 5px;
  display: inline-block;
}
.sp-pname { font-weight: 700; font-size: 14px; color: #221F1C; line-height: 1.3; }
.sp-cat-stack { display: flex; flex-direction: column; gap: 2px; line-height: 1.2; }
.sp-cat-primary   { font-size: 13px; font-weight: 700; color: #34302B; }
.sp-cat-secondary { font-size: 12px; color: #736C63; }
.sp-price {
  font-family: 'Sora', sans-serif;
  font-weight: 700; font-size: 14.5px;
  color: #221F1C;
}
.sp-strike { text-decoration: line-through; color: #A59D92; font-size: 13.5px; }
.sp-price-teal   { color: #0F6E6B; font-size: 15px; font-weight: 800; }
.sp-price-danger { color: #C93C30; font-size: 15px; font-weight: 800; }
.sp-price-orange { color: #D4620F; font-family: 'Sora', sans-serif; font-size: 15px; font-weight: 800; }
.is-expired { color: #C93C30; text-decoration: line-through; }

.sp-thumb {
  width: 46px; height: 46px; border-radius: 10px;
  overflow: hidden;
  background: linear-gradient(160deg, #F7F3EE, #EFEAE3);
  display: flex; align-items: center; justify-content: center;
  border: 1px solid #E5E1DA;
}
.sp-thumb img { max-width: 100%; max-height: 100%; object-fit: contain; }
.sp-thumb-empty { color: #C9C2B8; font-size: 18px; }

.sp-load-more {
  display: flex; align-items: center; justify-content: center;
  gap: 10px; padding: 14px; color: #736C63; font-weight: 600;
}
.sp-end-marker {
  text-align: center; padding: 12px;
  color: #A59D92; font-size: 13px; font-style: italic;
}

/* ============================================ */
/* BULK LIST                                     */
/* ============================================ */
.sp-bulk-list {
  max-height: 460px; overflow-y: auto;
  border: 1px solid #E5E1DA; border-radius: 12px;
  background: #FBF9F6;
}
.sp-bulk-group {
  padding: 9px 16px;
  font-size: 12px; font-weight: 800;
  letter-spacing: 0.05em; text-transform: uppercase;
  color: #4A4540; background: #F1EEE9;
  border-top: 1px solid #E5E1DA;
  border-bottom: 1px solid #E5E1DA;
  position: sticky; top: 0; z-index: 1;
}
.sp-bulk-item {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #F1EEE9;
  cursor: pointer; transition: background 0.15s;
}
.sp-bulk-item.is-indent { padding-left: 36px; }
.sp-bulk-item:hover { background: #fff; }
.sp-bulk-item.is-selected { background: #FFF1E4; }
.sp-bulk-body { flex: 1 1 auto; min-width: 0; }
.sp-bulk-title { font-size: 14px; font-weight: 700; color: #221F1C; }
.sp-bulk-sub { font-size: 12px; color: #A59D92; margin-top: 2px; }

.sp-bulk-confirm {
  display: flex; align-items: center; gap: 12px;
  margin-top: 14px; padding: 12px 16px;
  background: #FFF4DC; border: 1px solid #F2DBA5;
  border-radius: 12px; color: #7A520A;
  font-size: 14px; line-height: 1.5;
}
.sp-bulk-confirm strong { color: #7A520A; }

/* ============================================ */
/* CONFIG CTA                                    */
/* ============================================ */
.sp-config-cta {
  display: flex; align-items: center; gap: 16px;
  background: #fff;
  border: 1.5px solid #E5E1DA;
  border-radius: 18px;
  padding: 18px 22px;
  margin-top: 16px;
  transition: opacity 0.2s ease, border-color 0.2s ease;
}
.sp-config-cta.is-disabled { opacity: 0.55; }
.sp-config-cta-icon {
  width: 52px; height: 52px; border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #EE7A22, #D4620F);
  color: #fff; font-size: 22px; flex-shrink: 0;
}
.sp-config-cta-body { flex: 1 1 auto; min-width: 0; }
.sp-config-cta-title {
  font-family: 'Sora', sans-serif;
  font-size: 17px; font-weight: 700; color: #221F1C;
}
.sp-config-cta-sub { font-size: 13.5px; color: #736C63; margin-top: 2px; }
.sp-config-cta .btn { flex-shrink: 0; }
@media (max-width: 767px) {
  .sp-config-cta { flex-wrap: wrap; }
  .sp-config-cta .btn { width: 100%; }
}

/* ============================================ */
/* SUBMIT BAR                                    */
/* ============================================ */
.sp-submit-bar {
  position: sticky; bottom: 12px; z-index: 10;
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  background: #221F1C; color: #fff;
  padding: 16px 22px;
  border-radius: 18px;
  box-shadow: 0 20px 40px -14px rgba(34, 31, 28, 0.6);
  flex-wrap: wrap; margin-top: 16px;
}
.sp-submit-info { display: flex; align-items: center; gap: 10px; font-size: 15px; }
.sp-submit-info strong { color: #FFB06B; }
.sp-submit-actions { display: flex; gap: 10px; flex-wrap: wrap; }
.sp-submit-actions .btn { padding: 10px 24px; font-size: 15px; border-radius: 12px !important; }
@media (max-width: 767px) {
  .sp-submit-bar { flex-direction: column; align-items: stretch; }
  .sp-submit-actions { flex-direction: column; }
}

/* ============================================ */
/* VIEW TOOLBARS                                 */
/* ============================================ */
.sp-view-toolbar {
  display: flex; justify-content: space-between; align-items: center;
  flex-wrap: wrap; gap: 12px;
  background: #F7F5F2;
  padding: 12px 16px;
  border-radius: 14px; margin-bottom: 16px;
  border: 1px solid #E5E1DA;
}
.sp-view-toolbar-left { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; flex: 1 1 auto; min-width: 0; }
.sp-view-toolbar-right { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.sp-view-toolbar .sp-search { max-width: 320px; }

/* ============================================ */
/* EMPTY STATE / LOADER                          */
/* ============================================ */
.sp-empty { text-align: center; padding: 60px 20px; color: #A59D92; }
.sp-empty h5 { color: #4A4540; font-weight: 700; margin-top: 14px; }
.sp-empty p { margin: 0; color: #736C63; }
.sp-bulk-list .sp-empty { padding: 40px 20px; }
.sp-loading { text-align: center; padding: 60px 20px; color: #736C63; }
.sp-loading p { margin-top: 12px; }

/* ============================================ */
/* MODALS                                        */
/* ============================================ */
.sp-modal.modal-content {
  border: 0; border-radius: 20px; overflow: hidden;
  box-shadow: 0 30px 70px -20px rgba(34, 31, 28, 0.5);
}
.sp-modal .modal-header {
  background: #221F1C !important;
  color: #fff !important;
  border: 0 !important;
  border-bottom: 4px solid #EE7A22 !important;
  padding: 16px 24px !important;
}
.sp-modal .modal-header .close { color: #fff !important; opacity: 0.85; text-shadow: none; }
.sp-modal .modal-body { background: #F6F4F0; padding: 22px 24px; }
.sp-modal-title { display: flex; align-items: center; gap: 14px; }
.sp-modal-title-icon {
  width: 42px; height: 42px; border-radius: 12px;
  background: rgba(238, 122, 34, 0.2);
  color: #FFB06B;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 18px;
}
.sp-modal-title-icon.is-sale      { background: rgba(15, 110, 107, 0.2); color: #7FDACF; }
.sp-modal-title-icon.is-clearance { background: rgba(201, 60, 48, 0.22); color: #FFB0A5; }
.sp-modal-title-icon.is-promo     { background: rgba(238, 122, 34, 0.22); color: #FFB06B; }
.sp-modal-title-main { font-family: 'Sora', sans-serif; font-size: 18px; font-weight: 700; }
.sp-modal-title-sub { font-size: 13px; color: rgba(255, 255, 255, 0.65); font-family: 'Menlo', monospace; }
.sp-modal-body { display: flex; flex-direction: column; gap: 16px; }
.sp-modal .sp-readonly { background: #F1EEE9; }
.sp-modal-foot {
  display: flex; justify-content: flex-end; gap: 8px;
  padding-top: 16px; margin-top: 4px;
  border-top: 1px solid #E5E1DA;
}
.sp-modal-foot .btn { min-width: 130px; }

.sp-modal-scope-banner {
  display: flex; align-items: center; gap: 14px;
  padding: 14px 18px; border-radius: 14px;
  border: 1px solid #E5E1DA;
  background: #F7F5F2;
}
.sp-modal-scope-banner .b-icon { font-size: 22px; flex-shrink: 0; }
.sp-modal-scope-banner.is-sale      { background: #E2F2F0; border-color: #BFE1DD; color: #0B5552; }
.sp-modal-scope-banner.is-clearance { background: #FBE9E7; border-color: #F3C9C3; color: #8E2A21; }
.sp-modal-scope-banner.is-promo     { background: #FFF1E4; border-color: #F5CFAE; color: #B5520B; }
.sp-modal-scope-title { font-weight: 800; font-size: 15px; }
.sp-modal-scope-sub   { font-size: 13px; opacity: 0.85; margin-top: 2px; }

.sp-modal-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}
@media (max-width: 640px) { .sp-modal-grid { grid-template-columns: 1fr; } }

.sp-field { display: flex; flex-direction: column; gap: 6px; margin: 0; min-width: 0; }
.sp-field-label {
  font-size: 12px; font-weight: 800;
  color: #736C63;
  text-transform: uppercase; letter-spacing: 0.05em;
}
.sp-field-hint { font-size: 12.5px; color: #A59D92; }
.sp-pct {
  background: #F7F5F2; color: #4A4540;
  font-weight: 800; font-size: 16px;
  border-color: #E5E1DA;
}
.sp-readonly {
  padding: 12px 14px;
  background: #F7F5F2;
  border: 1.5px solid #E5E1DA;
  border-radius: 10px;
  font-size: 15px; color: #736C63; font-weight: 600;
}
.sp-datepicker { width: 100% !important; display: block; }
.sp-datepicker >>> .mx-input {
  height: 48px; font-size: 15px;
  border: 1.5px solid #E5E1DA;
  border-radius: 10px;
  font-weight: 600; padding: 0 14px;
  box-shadow: none;
}
.sp-field >>> .form-control {
  height: 48px; font-size: 15px; font-weight: 600;
  border: 1.5px solid #E5E1DA; border-radius: 10px;
}
.sp-field >>> .form-control:focus {
  border-color: #EE7A22;
  box-shadow: 0 0 0 3px rgba(238, 122, 34, 0.18);
}

/* ============================================ */
/* PREVIEW (editable)                            */
/* ============================================ */
.sp-preview {
  background: #FFF1E4;
  border: 1px solid #F5CFAE;
  border-radius: 12px;
  padding: 14px 18px;
}
.sp-preview-head {
  display: flex; align-items: center; gap: 8px;
  margin-bottom: 12px;
  color: #D4620F; font-size: 13.5px;
}
.sp-preview-hint {
  margin-left: auto;
  font-size: 12px;
  color: #B5520B;
  font-weight: 600;
}
.sp-preview-table { width: 100%; font-size: 14px; border-collapse: collapse; }
.sp-preview-table th {
  text-align: left;
  padding: 8px 10px;
  background: #FFE2C7;
  color: #B5520B;
  font-size: 11px;
  text-transform: uppercase;
  font-weight: 800; letter-spacing: 0.05em;
}
.sp-preview-table td {
  padding: 9px 10px;
  border-bottom: 1px solid #F5CFAE;
  vertical-align: middle;
}
.sp-preview-table tr:last-child td { border-bottom: 0; }
.sp-pct-sm {
  background: #fff;
  border-color: #F5CFAE;
  color: #B5520B;
  font-weight: 800;
  padding: 2px 8px;
  font-size: 12px;
}
.sp-preview-table >>> .form-control {
  height: 32px;
  font-size: 13px;
  font-weight: 700;
  border-color: #F5CFAE;
  padding: 2px 8px;
}
.sp-preview-table >>> .form-control:focus {
  border-color: #EE7A22;
  box-shadow: 0 0 0 2px rgba(238, 122, 34, 0.18);
}
.sp-preview-summary {
  display: flex; gap: 12px;
  margin-top: 10px;
  font-size: 12.5px;
  color: #B5520B;
  font-weight: 600;
}

.sp-preview-box {
  background: #E2F2F0;
  border: 1px solid #BFE1DD;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 14px; color: #0B5552;
}

/* Modal responsive */
@media (max-width: 575px) {
  .sp-dialog.modal-dialog { margin: 0; max-width: 100%; min-height: 100%; }
  .sp-modal.modal-content { border-radius: 0; min-height: 100%; }
  .sp-modal-foot { flex-direction: column-reverse; }
  .sp-modal-foot .btn { width: 100%; }
}

/* ============================================ */
/* POP ANIMATIONS                                */
/* ============================================ */
.sp-pop-enter-active { animation: spPopIn 0.28s cubic-bezier(0.34, 1.2, 0.64, 1); }
@keyframes spPopIn { from { transform: scale(0); opacity: 0; } to { transform: scale(1); opacity: 1; } }

/* ============================================ */
/* RESPONSIVE                                    */
/* ============================================ */
@media (max-width: 900px) {
  .sp-toolbar { flex-wrap: wrap; }
  .sp-filter { width: 100% !important; }
  .sp-search { max-width: 100%; flex: 1 1 100%; }
  .sp-vdivider { display: none; }
}
@media (max-width: 767px) {
  .sp-card { padding: 16px 14px; border-radius: 16px; }
  .sp-mode-tabs { width: 100%; }
  .sp-mode-tab { flex: 1; justify-content: center; padding: 10px 6px; font-size: 13px; }
  .sp-segment { width: 100%; overflow-x: auto; }
  .sp-view-toolbar-left { flex-direction: column; align-items: stretch; }
  .sp-view-toolbar-left > * { width: 100%; }
}
@media (prefers-reduced-motion: reduce) {
  .sp-stat, .sp-tab, .sp-pop-enter-active { transition: none !important; animation: none !important; }
}
</style>


// WEBPACK FOOTER //
// src/components/Products/SalePage.vue